import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { 
  Users, MapPin, Calendar, Plus, X, RefreshCw, Clock, CheckCircle2, 
  AlertCircle, Link2, Trash2, Edit2, ChevronDown, Bell, Timer,
  UserPlus, Layers, Target
} from "lucide-react";
import { toast } from "sonner";

export default function AssignmentsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [helpers, setHelpers] = useState([]);
  const [feeders, setFeeders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  
  const [showCreateAssignment, setShowCreateAssignment] = useState(false);
  const [showCreatePair, setShowCreatePair] = useState(false);
  const [activeTab, setActiveTab] = useState("assignments"); // assignments, pairs, notifications
  
  const [assignmentForm, setAssignmentForm] = useState({
    technician_id: "",
    assignment_type: "feeder", // feeder or dt
    feeder_code: "",
    dt_ids: [],
    duration_type: "days",
    duration_days: 7,
    notes: "",
    allowed_tasks: ["survey", "mi", "dtm", "dtc"]  // Default: all tasks
  });

  // Available tasks for assignment
  const AVAILABLE_TASKS = [
    { key: "survey", label: "Survey" },
    { key: "mi", label: "MI" },
    { key: "dtm", label: "DTM" },
    { key: "dtc", label: "DTC" }
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const [assignRes, pairRes, techRes, helperRes, feederRes, notifRes] = await Promise.all([
        api.get("/assignments?status=active"),
        api.get("/assignments/pairs"),
        api.get("/assignments/available-technicians"),
        api.get("/assignments/available-helpers"),
        api.get("/assignments/feeders-for-assignment"),
        api.get("/assignments/notifications")
      ]);
      setAssignments(assignRes.data.items || []);
      setPairs(pairRes.data.pairs || []);
      setTechnicians(techRes.data.technicians || []);
      setHelpers(helperRes.data.helpers || []);
      setFeeders(feederRes.data.feeders || []);
      setNotifications(notifRes.data.notifications || []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    if (!assignmentForm.technician_id) {
      toast.error("Please select a technician");
      return;
    }
    if (assignmentForm.assignment_type === "feeder" && !assignmentForm.feeder_code) {
      toast.error("Please select a feeder");
      return;
    }

    try {
      const payload = {
        technician_id: assignmentForm.technician_id,
        feeder_code: assignmentForm.assignment_type === "feeder" ? assignmentForm.feeder_code : null,
        dt_ids: assignmentForm.assignment_type === "dt" ? assignmentForm.dt_ids : null,
        duration_type: assignmentForm.duration_type,
        duration_days: assignmentForm.duration_type === "days" ? assignmentForm.duration_days : null,
        notes: assignmentForm.notes,
        allowed_tasks: assignmentForm.allowed_tasks
      };
      
      await api.post("/assignments", payload);
      toast.success("Assignment created!");
      setShowCreateAssignment(false);
      setAssignmentForm({
        technician_id: "",
        assignment_type: "feeder",
        feeder_code: "",
        dt_ids: [],
        duration_type: "days",
        duration_days: 7,
        notes: "",
        allowed_tasks: ["survey", "mi", "dtm", "dtc"]
      });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create assignment");
    }
  };

  const handleRevokeAssignment = async (id) => {
    if (!window.confirm("Revoke this assignment?")) return;
    try {
      await api.delete(`/assignments/${id}`);
      toast.success("Assignment revoked");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revoke");
    }
  };

  const handleExtendAssignment = async (id) => {
    const days = prompt("Extend by how many days?", "7");
    if (!days) return;
    try {
      await api.post(`/assignments/${id}/extend?days=${parseInt(days)}`);
      toast.success(`Extended by ${days} days`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to extend");
    }
  };

  const handleCreatePair = async (techId, helperId) => {
    try {
      await api.post("/assignments/pairs", { technician_id: techId, helper_id: helperId });
      toast.success("Pair created!");
      setShowCreatePair(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create pair");
    }
  };

  const handleUpdatePairStatus = async (pairId, status) => {
    try {
      await api.put(`/assignments/pairs/${pairId}`, { status });
      toast.success(`Pair status: ${status}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update pair");
    }
  };

  const handleDeletePair = async (pairId) => {
    if (!window.confirm("Delete this pair?")) return;
    try {
      await api.delete(`/assignments/pairs/${pairId}`);
      toast.success("Pair deleted");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete pair");
    }
  };

  const getDurationLabel = (assignment) => {
    if (assignment.duration_type === "today") return "Today only";
    if (assignment.duration_type === "till_saturation") return "Till Saturation";
    if (assignment.duration_type === "days") return `${assignment.duration_days} days`;
    return assignment.duration_type;
  };

  const getStatusColor = (status) => {
    if (status === "active") return "bg-green-100 text-green-700";
    if (status === "expired") return "bg-red-100 text-red-700";
    if (status === "completed") return "bg-blue-100 text-blue-700";
    return "bg-slate-100 text-slate-700";
  };

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="assignments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>
            Feeder & DT Assignments
          </h1>
          <p className="text-slate-500 text-sm">Manage persistent feeder/DT assignments and technician-helper pairs</p>
        </div>
        <div className="flex items-center gap-3">
          {notifications.length > 0 && (
            <button 
              onClick={() => setActiveTab("notifications")}
              className="relative p-2 border border-orange-200 rounded-sm bg-orange-50 hover:bg-orange-100"
            >
              <Bell size={18} className="text-orange-600" />
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                {notifications.length}
              </span>
            </button>
          )}
          <button onClick={fetchData} className="p-2 border border-slate-200 rounded-sm hover:bg-slate-50">
            <RefreshCw size={16} className={loading ? "animate-spin text-orange-500" : "text-slate-500"} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab("assignments")}
          className={`px-4 py-2 rounded-t-sm font-semibold text-sm transition ${
            activeTab === "assignments" ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          <Layers size={14} className="inline mr-2" />
          Assignments ({assignments.length})
        </button>
        <button
          onClick={() => setActiveTab("pairs")}
          className={`px-4 py-2 rounded-t-sm font-semibold text-sm transition ${
            activeTab === "pairs" ? "bg-purple-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          <Link2 size={14} className="inline mr-2" />
          Pairs ({pairs.length})
        </button>
        <button
          onClick={() => setActiveTab("notifications")}
          className={`px-4 py-2 rounded-t-sm font-semibold text-sm transition ${
            activeTab === "notifications" ? "bg-red-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
          }`}
        >
          <Bell size={14} className="inline mr-2" />
          Alerts ({notifications.length})
        </button>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
          <RefreshCw size={24} className="mx-auto animate-spin text-orange-500 mb-2" />
          <p className="text-slate-500">Loading...</p>
        </div>
      ) : (
        <>
          {/* Assignments Tab */}
          {activeTab === "assignments" && (
            <div className="space-y-4">
              {/* Action Bar */}
              <div className="flex justify-between items-center">
                <div className="text-sm text-slate-500">
                  {assignments.length} active assignment(s)
                </div>
                <button
                  onClick={() => setShowCreateAssignment(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-sm font-bold text-sm hover:bg-orange-600"
                  data-testid="create-assignment-btn"
                >
                  <Plus size={16} />
                  New Assignment
                </button>
              </div>

              {/* Assignments Grid */}
              {assignments.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
                  <Target size={48} className="mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-bold text-slate-600 mb-2">No Active Assignments</h3>
                  <p className="text-slate-400 mb-4">Create assignments to give technicians access to feeders or specific DTs</p>
                  <button
                    onClick={() => setShowCreateAssignment(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-sm font-bold text-sm"
                  >
                    <Plus size={16} />
                    Create First Assignment
                  </button>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {assignments.map((a) => (
                    <div key={a.id} className="bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-md transition">
                      {/* Header */}
                      <div className={`px-4 py-3 ${a.assignment_type === "feeder" ? "bg-gradient-to-r from-green-500 to-green-600" : "bg-gradient-to-r from-blue-500 to-blue-600"} text-white`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold uppercase opacity-80">
                            {a.assignment_type === "feeder" ? "Feeder Assignment" : "DT Assignment"}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${getStatusColor(a.status)}`}>
                            {a.status}
                          </span>
                        </div>
                        <div className="text-lg font-bold mt-1">
                          {a.feeder_code || `${a.dt_count} DTs`}
                        </div>
                        {a.feeder_name && (
                          <div className="text-sm opacity-80">{a.feeder_name}</div>
                        )}
                      </div>

                      {/* Body */}
                      <div className="p-4 space-y-3">
                        {/* Technician */}
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white font-bold text-xs">
                            {a.technician_name?.charAt(0) || "T"}
                          </div>
                          <div>
                            <div className="font-semibold text-slate-800 text-sm">{a.technician_name}</div>
                            <div className="text-xs text-slate-500">Technician</div>
                          </div>
                        </div>

                        {/* Paired Helper */}
                        {a.paired_helper && (
                          <div className="flex items-center gap-2 pl-4 border-l-2 border-purple-200">
                            <div className="w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold text-[10px]">
                              {a.paired_helper?.charAt(0) || "H"}
                            </div>
                            <div className="text-xs text-purple-600">+ {a.paired_helper} (Helper)</div>
                          </div>
                        )}

                        {/* Duration */}
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Timer size={14} />
                          <span>{getDurationLabel(a)}</span>
                        </div>

                        {/* DT Count */}
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <MapPin size={14} />
                          <span>{a.dt_count || 0} DTs in scope</span>
                        </div>

                        {/* Dates */}
                        <div className="text-xs text-slate-400">
                          Started: {new Date(a.start_date).toLocaleDateString()}
                          {a.end_date && ` • Ends: ${new Date(a.end_date).toLocaleDateString()}`}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
                        <button
                          onClick={() => handleExtendAssignment(a.id)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                          title="Extend"
                        >
                          <Clock size={16} />
                        </button>
                        <button
                          onClick={() => handleRevokeAssignment(a.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Revoke"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Pairs Tab */}
          {activeTab === "pairs" && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="text-sm text-slate-500">{pairs.length} pair(s)</div>
                <button
                  onClick={() => setShowCreatePair(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-sm font-bold text-sm hover:bg-purple-600"
                  data-testid="create-pair-btn"
                >
                  <UserPlus size={16} />
                  Create Pair
                </button>
              </div>

              {pairs.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
                  <Link2 size={48} className="mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-bold text-slate-600 mb-2">No Pairs Created</h3>
                  <p className="text-slate-400 mb-4">Pair technicians with helpers for field work</p>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {pairs.map((pair) => (
                    <div key={pair.id} className="bg-white border border-slate-200 rounded-sm p-4">
                      <div className="flex items-center gap-4">
                        {/* Technician */}
                        <div className="flex-1">
                          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold mx-auto mb-2">
                            {pair.technician_name?.charAt(0)}
                          </div>
                          <div className="text-center">
                            <div className="font-semibold text-sm">{pair.technician_name}</div>
                            <div className="text-xs text-green-600">Technician</div>
                          </div>
                        </div>

                        <Link2 size={20} className="text-slate-300" />

                        {/* Helper */}
                        <div className="flex-1">
                          <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold mx-auto mb-2">
                            {pair.helper_name?.charAt(0)}
                          </div>
                          <div className="text-center">
                            <div className="font-semibold text-sm">{pair.helper_name}</div>
                            <div className="text-xs text-purple-600">Helper</div>
                          </div>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="mt-4 flex items-center justify-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          pair.status === "active" ? "bg-green-100 text-green-700" :
                          pair.status === "absent_tech" ? "bg-yellow-100 text-yellow-700" :
                          pair.status === "absent_helper" ? "bg-yellow-100 text-yellow-700" :
                          "bg-red-100 text-red-700"
                        }`}>
                          {pair.status === "absent_tech" ? "Tech Absent" :
                           pair.status === "absent_helper" ? "Helper Absent" :
                           pair.status.toUpperCase()}
                        </span>
                      </div>

                      {/* Actions */}
                      <div className="mt-4 pt-3 border-t border-slate-100 flex justify-center gap-2">
                        {pair.status === "active" && (
                          <>
                            <button
                              onClick={() => handleUpdatePairStatus(pair.id, "absent_tech")}
                              className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                            >
                              Tech Absent
                            </button>
                            <button
                              onClick={() => handleUpdatePairStatus(pair.id, "absent_helper")}
                              className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                            >
                              Helper Absent
                            </button>
                          </>
                        )}
                        {pair.status !== "active" && (
                          <button
                            onClick={() => handleUpdatePairStatus(pair.id, "active")}
                            className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            Reactivate
                          </button>
                        )}
                        <button
                          onClick={() => handleDeletePair(pair.id)}
                          className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                        >
                          Break Pair
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === "notifications" && (
            <div className="space-y-4">
              {notifications.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
                  <CheckCircle2 size={48} className="mx-auto text-green-300 mb-4" />
                  <h3 className="text-lg font-bold text-slate-600">All Clear!</h3>
                  <p className="text-slate-400">No pending notifications</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {notifications.map((n) => (
                    <div key={n.id} className={`bg-white border rounded-sm p-4 flex items-center gap-4 ${
                      n.type === "expired" ? "border-red-200" : "border-blue-200"
                    }`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        n.type === "expired" ? "bg-red-100" : "bg-blue-100"
                      }`}>
                        {n.type === "expired" ? (
                          <AlertCircle size={20} className="text-red-600" />
                        ) : (
                          <CheckCircle2 size={20} className="text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-slate-800">
                          {n.type === "expired" ? "Assignment Expired" : "Assignment Saturated"}
                        </div>
                        <div className="text-sm text-slate-500">
                          {n.feeder_code || "DT Assignment"} • {new Date(n.created_at).toLocaleString()}
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          await api.put(`/assignments/notifications/${n.id}/read`);
                          fetchData();
                        }}
                        className="px-3 py-1 text-xs bg-slate-100 text-slate-600 rounded hover:bg-slate-200"
                      >
                        Dismiss
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Create Assignment Modal */}
      {showCreateAssignment && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-sm p-6 w-full max-w-lg shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>Create Assignment</h3>
              <button onClick={() => setShowCreateAssignment(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateAssignment} className="space-y-4">
              {/* Technician Selection */}
              <div>
                <label className="field-label">Technician *</label>
                <select
                  className="field-select"
                  value={assignmentForm.technician_id}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, technician_id: e.target.value })}
                  required
                  data-testid="select-assignment-tech"
                >
                  <option value="">Select Technician</option>
                  {technicians.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.full_name} {t.is_paired ? "👥" : ""} ({t.active_assignments} assignments)
                    </option>
                  ))}
                </select>
              </div>

              {/* Assignment Type */}
              <div>
                <label className="field-label">Assignment Type *</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="assignment_type"
                      value="feeder"
                      checked={assignmentForm.assignment_type === "feeder"}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_type: e.target.value })}
                      className="text-orange-500"
                    />
                    <span className="text-sm">Entire Feeder</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="assignment_type"
                      value="dt"
                      checked={assignmentForm.assignment_type === "dt"}
                      onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_type: e.target.value })}
                      className="text-orange-500"
                    />
                    <span className="text-sm">Specific DTs</span>
                  </label>
                </div>
              </div>

              {/* Feeder Selection */}
              {assignmentForm.assignment_type === "feeder" && (
                <div>
                  <label className="field-label">Feeder *</label>
                  <select
                    className="field-select"
                    value={assignmentForm.feeder_code}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, feeder_code: e.target.value })}
                    required
                    data-testid="select-assignment-feeder"
                  >
                    <option value="">Select Feeder</option>
                    {feeders.map((f) => (
                      <option key={f.feeder_code} value={f.feeder_code}>
                        {f.feeder_code} - {f.feeder_name} ({f.pending_dts}/{f.total_dts} pending)
                        {f.assigned_to?.length > 0 && ` [${f.assigned_to.join(", ")}]`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Duration */}
              <div>
                <label className="field-label">Duration *</label>
                <select
                  className="field-select"
                  value={assignmentForm.duration_type}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, duration_type: e.target.value })}
                >
                  <option value="today">Today Only</option>
                  <option value="days">Number of Days</option>
                  <option value="till_saturation">Till Saturation (QC Passed / Hold)</option>
                </select>
              </div>

              {assignmentForm.duration_type === "days" && (
                <div>
                  <label className="field-label">Number of Days</label>
                  <input
                    type="number"
                    className="field-input"
                    min="1"
                    max="365"
                    value={assignmentForm.duration_days}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, duration_days: parseInt(e.target.value) })}
                  />
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="field-label">Notes</label>
                <textarea
                  className="field-input"
                  rows={2}
                  placeholder="Any special instructions..."
                  value={assignmentForm.notes}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, notes: e.target.value })}
                />
              </div>

              {/* Allowed Tasks */}
              <div className="border-t border-slate-200 pt-4">
                <label className="field-label mb-2">Allowed Tasks</label>
                <p className="text-[10px] text-slate-500 mb-3">
                  Select which tasks the technician can perform on this assignment
                </p>
                <div className="grid grid-cols-4 gap-2">
                  {AVAILABLE_TASKS.map(task => (
                    <label 
                      key={task.key} 
                      className={`flex items-center gap-1.5 p-2 border rounded-sm cursor-pointer transition-colors ${
                        assignmentForm.allowed_tasks.includes(task.key) 
                          ? "border-orange-300 bg-orange-50" 
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={assignmentForm.allowed_tasks.includes(task.key)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAssignmentForm({
                              ...assignmentForm,
                              allowed_tasks: [...assignmentForm.allowed_tasks, task.key]
                            });
                          } else {
                            setAssignmentForm({
                              ...assignmentForm,
                              allowed_tasks: assignmentForm.allowed_tasks.filter(t => t !== task.key)
                            });
                          }
                        }}
                        className="rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-xs font-semibold text-slate-700">{task.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateAssignment(false)}
                  className="flex-1 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600"
                  data-testid="submit-assignment-btn"
                >
                  Create Assignment
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Create Pair Modal */}
      {showCreatePair && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-sm p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ fontFamily: "Manrope" }}>Create Pair</h3>
              <button onClick={() => setShowCreatePair(false)} className="p-1 hover:bg-slate-100 rounded">
                <X size={18} />
              </button>
            </div>

            <PairCreator
              technicians={technicians.filter(t => !t.is_paired)}
              helpers={helpers.filter(h => !h.is_paired)}
              onSubmit={handleCreatePair}
              onCancel={() => setShowCreatePair(false)}
            />
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Pair Creator Sub-component
function PairCreator({ technicians, helpers, onSubmit, onCancel }) {
  const [selectedTech, setSelectedTech] = useState("");
  const [selectedHelper, setSelectedHelper] = useState("");

  return (
    <div className="space-y-4">
      <div>
        <label className="field-label">Technician</label>
        <select
          className="field-select"
          value={selectedTech}
          onChange={(e) => setSelectedTech(e.target.value)}
          data-testid="pair-select-tech"
        >
          <option value="">Select Technician</option>
          {technicians.map((t) => (
            <option key={t.id} value={t.id}>{t.full_name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="field-label">Helper</label>
        <select
          className="field-select"
          value={selectedHelper}
          onChange={(e) => setSelectedHelper(e.target.value)}
          data-testid="pair-select-helper"
        >
          <option value="">Select Helper</option>
          {helpers.map((h) => (
            <option key={h.id} value={h.id}>{h.full_name}</option>
          ))}
        </select>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => onSubmit(selectedTech, selectedHelper)}
          disabled={!selectedTech || !selectedHelper}
          className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-sm text-sm font-bold hover:bg-purple-600 disabled:opacity-50"
          data-testid="submit-pair-btn"
        >
          Create Pair
        </button>
      </div>
    </div>
  );
}
